import React, { useCallback, useRef } from 'react';
import { styled } from '@compiled/react';
import { mergeRefs } from 'use-callback-ref';
import Button from '@atlaskit/button';
import DropdownMenu, {
	DropdownItem,
	DropdownItemGroup,
	type CustomTriggerProps,
} from '@atlaskit/dropdown-menu';
import ClockIcon from '@atlaskit/icon/core/clock';
import ChevronRightLargeIcon from '@atlaskit/icon/glyph/chevron-right-large';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import HipchatChevronDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useCommonPageContext } from '@atlassian/jira-servicedesk-common-page-context';
import { usePopupController } from '@atlassian/jira-servicedesk-common/src/use-popup-controller';
import { EDIT_MODE, COLLAPSED_MODE } from '../../../common/constants';
import type { Mode, Metrics, Config } from '../../../common/types';
import { useSLAHierarchicalGoalsEnabled } from '../../../controllers/enable-sla-hierarchical-goals';
import { useGoalCounter } from '../../../controllers/goal-counter';
import { useMetricActions } from '../../../controllers/metric-actions';
import { DeleteMetricModal } from './delete-metric-modal';
import messages from './messages';

type Props = {
	id: string;
	name: string;
	mode: Mode;
	setIsInitialSlaPageLoadToFalse: () => void;
	config: Config;
	metrics: Metrics;
};

const SummaryView = ({
	id,
	name,
	mode,
	setIsInitialSlaPageLoadToFalse,
	config,
	metrics,
}: Props) => {
	const { formatMessage, formatNumber } = useIntl();
	const [, { updatePath }] = useMetricActions();
	const { baseUrl, projectKey } = useCommonPageContext();
	const [isDeleteModalOpen, toggleDeleteModal] = usePopupController(false);
	const [, actions] = useGoalCounter();

	const handleToggleView = useCallback(() => {
		// @ts-expect-error - TS2367 - This condition will always return 'false' since the types 'Mode' and '"collapsed"' have no overlap.
		const metricId = mode === COLLAPSED_MODE ? id : null;
		setIsInitialSlaPageLoadToFalse();
		updatePath(metricId);
	}, [id, mode, updatePath, setIsInitialSlaPageLoadToFalse]);

	const handleEditMetricClick = useCallback(() => {
		const calculateTotalNumberOfGoalsInProject = () => {
			let goalCount = 0;
			Object.keys(metrics).forEach((key) => {
				goalCount += Object.keys(metrics[key].config.goals).length;
			});
			actions.setGoalCounter(goalCount - Object.keys(metrics).length);
		};
		updatePath(id, EDIT_MODE);
		calculateTotalNumberOfGoalsInProject();
	}, [id, updatePath, metrics, actions]);

	const { goals } = config;
	const [{ isPriorityNameChanged }] = useSLAHierarchicalGoalsEnabled();
	const actionButtonRef = useRef<HTMLButtonElement>(null);

	return (
		<HeadingContainer>
			<TextContainer>
				<Box xcss={iconContainerStyles}>
					<ClockIcon
						LEGACY_size="large"
						LEGACY_primaryColor={token('color.icon', colors.N500)}
						label=""
						color="currentColor"
					/>
				</Box>
				{name}{' '}
				{
					// @ts-expect-error TS-7053
					isPriorityNameChanged[id] !== undefined ? (
						<Tooltip content={formatMessage(messages.errorTooltip)} position="top">
							<Button
								iconBefore={
									<ErrorIcon
										primaryColor={token('color.icon.danger', colors.R500)}
										size="medium"
										label=""
									/>
								}
								appearance="subtle"
								aria-label={formatMessage(messages.errorTooltip)}
							/>
						</Tooltip>
					) : null
				}
			</TextContainer>
			<ActionsContainer>
				{/* subtracting 1 from goal length for removing the count for default goal. */}
				{Object.keys(goals).length - 1 <= 1 ? (
					<p>
						{formatNumber(Object.keys(goals).length - 1)}&nbsp;
						{formatMessage(messages.goal)}
					</p>
				) : (
					<p>
						{formatNumber(Object.keys(goals).length - 1)}&nbsp;
						{formatMessage(messages.goals)}
					</p>
				)}
				&nbsp;
				<Box xcss={buttonContainerStyles}>
					<Button
						onClick={handleEditMetricClick}
						spacing="compact"
						testId={`servicedesk-sla-settings.ui.metrics.summary-with-actions.edit-button-${id}`}
					>
						{formatMessage(messages.editMetric)}
					</Button>
				</Box>
				<DropdownMenu
					shouldFlip={false}
					placement="bottom-end"
					shouldRenderToParent
					trigger={({ triggerRef, ...props }: CustomTriggerProps) => (
						<Button
							{...props}
							appearance="subtle"
							spacing="compact"
							iconBefore={<MoreIcon label={formatMessage(messages.more)} size="medium" />}
							data-test-id={`servicedesk-sla-settings.ui.metrics.summary-with-actions.dropdown-${id}`}
							ref={mergeRefs([actionButtonRef, triggerRef])}
						/>
					)}
				>
					<DropdownItemGroup>
						<DropdownItem
							onClick={toggleDeleteModal}
							data-test-id="servicedesk-sla-settings.ui.metrics.summary-with-actions.delete-dropdown-item"
						>
							{formatMessage(messages.deleteMetric)}
						</DropdownItem>
					</DropdownItemGroup>
				</DropdownMenu>
				{/* @ts-expect-error - TS2367 - This condition will always return 'false' since the types 'Mode' and '"collapsed"' have no overlap. */}
				{mode === COLLAPSED_MODE ? (
					<Button
						appearance="subtle-link"
						onClick={handleToggleView}
						testId={`servicedesk-sla-settings.ui.metrics.summary-with-actions.view-button-${id}`}
					>
						<ChevronContainer>
							<ChevronRightLargeIcon label={formatMessage(messages.closeMetric)} />
						</ChevronContainer>
					</Button>
				) : (
					<Button appearance="subtle-link" onClick={handleToggleView}>
						<ChevronContainer>
							{}
							<HipchatChevronDownIcon label={formatMessage(messages.expandMetric)} />
						</ChevronContainer>
					</Button>
				)}
			</ActionsContainer>
			<DeleteMetricModal
				key={id}
				isOpen={isDeleteModalOpen}
				onClose={() => {
					toggleDeleteModal();
					setTimeout(() => {
						actionButtonRef?.current?.focus();
					}, 0);
				}}
				metricId={id}
				metricName={name}
				// @ts-expect-error(TS2322 baseUrl, projectId or projectKey can be undefined, if usePageContext() returns null. Please handle undefined value in your code) - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
				baseUrl={baseUrl}
				// @ts-expect-error(TS2322 baseUrl, projectId or projectKey can be undefined, if usePageContext() returns null. Please handle undefined value in your code) - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
				projectKey={projectKey}
			/>
		</HeadingContainer>
	);
};

export const Summary = SummaryView;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingContainer = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 7}px`,
	paddingLeft: token('space.200', '16px'),
});

const iconContainerStyles = xcss({
	paddingRight: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChevronContainer = styled.div({
	paddingTop: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextContainer = styled.div({
	font: token('font.body.large'),
	fontWeight: token('font.weight.medium'),
	fontStretch: 'normal',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N700),
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsContainer = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N500),
	alignItems: 'center',
});

const buttonContainerStyles = xcss({
	marginRight: 'space.100',
});
